import { Reference } from '@apollo/client';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useCallback } from 'react';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ChannelPinFragmentDoc,
  useCreateChannelPinMutation,
  useDeleteChannelPinByUserAndChannelMutation,
} from 'shared/types/graphql';
import * as Sentry from '@sentry/react';
import { useTracking } from 'shared/providers/TrackingProvider';

interface UsePinChannelInput {
  channelId: string;
  isChannelPinned: boolean;
}

interface UsePinChannelOutput {
  togglePin: () => Promise<void>;
}

const usePinChannel = ({ channelId, isChannelPinned }: UsePinChannelInput): UsePinChannelOutput => {
  const { trackEvent } = useTracking();
  const { clinicUserId } = useClinicUser();
  const toast = useToast();

  const [createChannelPin] = useCreateChannelPinMutation({
    refetchQueries: ['getChannelListChannels', 'getPinnedChannelListChannels'],
    update: (cache, result) => {
      const newPin = result.data?.createOneChannelPin;
      if (newPin?.id) {
        const newPinRef = cache.writeFragment({
          data: newPin,
          fragment: ChannelPinFragmentDoc,
        });

        cache.modify({
          id: cache.identify({ __typename: 'Channel', id: channelId }),
          fields: {
            channelPins(existingChannelPinsRefs: ReadonlyArray<Reference> = [], { readField }) {
              const pinExists = existingChannelPinsRefs.some((ref) => readField('id', ref) === newPin.id);

              if (pinExists) {
                return existingChannelPinsRefs; // Return the existing refs if the pin is already there to avoid duplicates
              }

              // Return the updated array of refs with the new pin ref added
              return [...existingChannelPinsRefs, newPinRef];
            },
          },
        });
      }
    },
  });

  const [deleteChannelPin] = useDeleteChannelPinByUserAndChannelMutation({
    refetchQueries: ['getChannelListChannels', 'getPinnedChannelListChannels'],
    update: (cache, result): void => {
      if (result.data?.deleteManyChannelPin?.count) {
        cache.modify({
          id: cache.identify({ __typename: 'Channel', id: channelId }),
          fields: {
            channelPins(existingChannelPinsRefs: ReadonlyArray<Reference> = [], { readField }) {
              return existingChannelPinsRefs.filter(
                (channelPinRef) => readField('userId', channelPinRef) !== clinicUserId,
              );
            },
          },
        });
      }
    },
  });

  const handleTogglePin = useCallback(async () => {
    try {
      const userId = clinicUserId || '';
      if (isChannelPinned) {
        await deleteChannelPin({
          variables: { userId, channelId },
        });
        toast({ status: 'info', title: 'Conversation unpinned', hideIcon: true });
      } else {
        await createChannelPin({
          variables: { userId, channelId },
        });
        toast({ status: 'info', title: 'Conversation pinned', hideIcon: true });
      }
      trackEvent(ConversationEvents.ChannelListPinToggle, { pinned: !isChannelPinned });
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [clinicUserId, isChannelPinned, trackEvent, deleteChannelPin, channelId, toast, createChannelPin]);

  return {
    togglePin: handleTogglePin,
  };
};

export default usePinChannel;
